import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download, RefreshCw, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { MonthPicker } from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import {
  IMS_CSV_HEADERS,
  downloadImsCsv,
  lastMonth,
  monthLabel,
  useIms,
  useImsMutations,
  type ImsAction,
  type ImsMatch,
  type ImsRow,
  type ImsView,
} from '@/lib/gst';
import { i18n, useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { ltrIn } from '@/lib/sales';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/gst/ims')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Ims,
});

const MATCH_TONE: Record<ImsMatch, string> = {
  matched: 'bg-[#ECFDF3] text-[#067647]',
  probable: 'bg-[#EFF8FF] text-[#175CD3]',
  mismatch: 'bg-[#FFFAEB] text-[#B54708]',
  not_in_books: 'bg-[#FEF3F2] text-[#B42318]',
};
/** A match status in the reader's language. */
const matchLabel = (m: ImsMatch): string =>
  i18n.t(`taxPages.ims.match.${m === 'not_in_books' ? 'notInBooks' : m}`);
/** An action in the reader's language. */
const actionLabel = (a: ImsAction): string =>
  i18n.t(`taxPages.ims.action.${a === 'no_action' ? 'noAction' : a}`);
const CHIPS = ['all', 'matched', 'probable', 'mismatch', 'not_in_books', 'open', 'reject'] as const;
type Chip = (typeof CHIPS)[number];
const chipLabel = (k: Chip): string =>
  k === 'all'
    ? i18n.t('taxPages.einvoice.filter.all')
    : k === 'probable' || k === 'open'
      ? i18n.t(`taxPages.ims.chip.${k}`)
      : k === 'reject'
        ? actionLabel('reject')
        : k === 'matched' || k === 'mismatch'
          ? matchLabel(k)
          : matchLabel('not_in_books');
/** A difference's field name in the reader's language (English keeps the API's identifier). */
const FIELDS = [
  'docType',
  'number',
  'date',
  'taxable',
  'igst',
  'cgst',
  'sgst',
  'cess',
  'total',
] as const;
const fieldLabel = (f: string): string =>
  (FIELDS as readonly string[]).includes(f)
    ? i18n.t(`taxPages.ims.field.${f as (typeof FIELDS)[number]}`)
    : f;

/** GST of all heads in paise (bigint: amounts never go through a float). */
const tax = (r: { igst: string; cgst: string; sgst: string; cess: string }) =>
  [r.igst, r.cgst, r.sgst, r.cess].reduce((a, x) => a + (toMinor(x) ?? 0n), 0n);
const rupees = (paise: bigint) => formatMinor(paise);

/**
 * Screen T12 (document 06): Taxes › GST › IMS and GSTR-2B. Imports the portal's GSTR-2B JSON (or
 * the CSV template), matches each supplier document to the bills in the books, and keeps the
 * accept / reject / pending actions as a worksheet: no GSP is connected, so the actions are taken
 * on the portal's IMS dashboard by hand.
 */
function Ims() {
  const t = useT();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const { taxPack } = useOrgLocale();
  const period = Route.useSearch().period ?? lastMonth();
  const q = useIms(period);
  const m = useImsMutations(period);
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const file = useRef<HTMLInputElement>(null);

  const onFile = async (f: File) => {
    // the API takes up to 4.5 MB of file content (ImsImportDto); a larger body is refused unread
    if (f.size > 4_500_000)
      return toast(t('taxPages.ims.tooLarge', { name: ltrIn(f.name) }), {
        tone: 'error',
      });
    const content = await f.text();
    const source =
      /\.csv$/i.test(f.name) || !content.trimStart().startsWith('{') ? 'csv' : 'gstr2b';
    m.importFile.mutate(
      { source, fileName: f.name, content },
      {
        onSuccess: (v) => {
          toast(t('taxPages.ims.imported', { n: v.rows.length, month: monthLabel(v.period) }));
          if (v.period !== period)
            void navigate({ to: '/taxes/gst/ims', search: { period: v.period } });
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
      },
    );
  };

  return (
    <>
      <PageHeader
        title={t('taxPages.gst.imsLink')}
        description={t('taxPages.ims.description')}
        actions={
          taxPack === 'in' && orgId ? (
            <>
              <MonthPicker
                period={period}
                onChange={(p) => void navigate({ to: '/taxes/gst/ims', search: { period: p } })}
              />
              {staff ? (
                <>
                  <input
                    ref={file}
                    type="file"
                    accept=".json,.csv,application/json,text/csv"
                    className="hidden"
                    aria-label={t('taxPages.ims.fileAria')}
                    data-testid="ims-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onFile(f);
                      e.target.value = '';
                    }}
                  />
                  <Button disabled={m.importFile.isPending} onClick={() => file.current?.click()}>
                    <Upload className="size-4" />{' '}
                    {m.importFile.isPending
                      ? t('taxPages.ims.importing')
                      : t('taxPages.ims.importButton')}
                  </Button>
                </>
              ) : null}
            </>
          ) : undefined
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          {t('taxPages.gst.title')}
        </Link>{' '}
        › {monthLabel(period)}
      </p>
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : taxPack !== 'in' ? (
        <EmptyState title={t('taxPages.ims.indiaOnly')} />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : q.isError ? (
        <EmptyState title={t('taxPages.corporateTax.loadError')} hint={q.error.message} />
      ) : (
        <Body v={q.data} period={period} staff={staff} m={m} />
      )}
    </>
  );
}

function Body({
  v,
  period,
  staff,
  m,
}: {
  v: ImsView;
  period: string;
  staff: boolean;
  m: ReturnType<typeof useImsMutations>;
}) {
  const t = useT();
  const orgId = useOrgId()!;
  const { currency } = useOrgLocale();
  const [chip, setChip] = useState<Chip>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const rows = useMemo(
    () =>
      v.rows.filter((r) =>
        chip === 'all'
          ? true
          : chip === 'open'
            ? r.action === 'no_action'
            : chip === 'reject'
              ? r.action === 'reject'
              : r.matchStatus === chip,
      ),
    [v.rows, chip],
  );
  const act = (action: ImsAction, ids = [...picked]) => {
    const remark =
      action === 'reject'
        ? (window.prompt(t('taxPages.ims.rejectPrompt')) ?? undefined)
        : undefined;
    if (action === 'reject' && remark === undefined) return;
    if (remark && remark.trim().length > 200)
      return toast(t('taxPages.ims.reasonTooLong'), { tone: 'error' });
    m.act.mutate(
      { ids, action, ...(remark?.trim() ? { remark: remark.trim() } : {}) },
      {
        onSuccess: (r) => {
          toast(
            action === 'accept'
              ? t('taxPages.ims.markedAccept', { count: r.updated })
              : action === 'reject'
                ? t('taxPages.ims.markedReject', { count: r.updated })
                : action === 'pending'
                  ? t('taxPages.ims.markedPending', { count: r.updated })
                  : t('taxPages.ims.markedNoAction', { count: r.updated }),
          );
          setPicked(new Set());
        },
        onError: (e) => toast(e.message, { tone: 'error' }),
      },
    );
  };
  const template = () => {
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${IMS_CSV_HEADERS.join(',')}\r\n`], { type: 'text/csv' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Inward-supplies-template.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!v.imports.length)
    return (
      <EmptyState
        title={t('taxPages.ims.noImportTitle', { month: monthLabel(period) })}
        hint={t('taxPages.ims.noImportHint')}
        action={
          <Button variant="outline" onClick={template}>
            <Download className="size-4" /> {t('taxPages.ims.csvTemplate')}
          </Button>
        }
      />
    );

  const itc = v.summary.itc.itcAvailable;
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));
  return (
    <div className="flex flex-col gap-4">
      <div
        className="flex items-start gap-2 rounded-md border border-[#FCD34D] bg-[#FFFBEB] px-4 py-2.5 text-sm"
        data-testid="ims-worksheet-note"
      >
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[#B45309]" aria-hidden />
        <span>{t('taxPages.ims.worksheetNote')}</span>
      </div>

      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
        data-testid="ims-summary"
      >
        <Stat
          label={matchLabel('matched')}
          value={v.summary.matched}
          onClick={() => setChip('matched')}
        />
        <Stat
          label={chipLabel('probable')}
          value={v.summary.probable}
          onClick={() => setChip('probable')}
        />
        <Stat
          label={matchLabel('mismatch')}
          value={v.summary.mismatch}
          onClick={() => setChip('mismatch')}
        />
        <Stat
          label={matchLabel('not_in_books')}
          value={v.summary.notInBooks}
          onClick={() => setChip('not_in_books')}
        />
        <Stat label={t('taxPages.ims.notOnPortal')} value={v.summary.notOnPortal} />
        <div className="rounded-lg border bg-card px-3 py-2">
          <div className="text-xs text-muted-foreground">{t('taxPages.ims.itcCompare')}</div>
          <div className="tnum text-sm font-semibold">
            {currency} {rupees(tax(itc))}
          </div>
          <div className="tnum text-xs text-muted-foreground">
            {currency} {rupees(tax(v.summary.booksItc))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label={t('taxPages.ims.filterAria')}
        >
          {CHIPS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={chip === k}
              onClick={() => setChip(k)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                chip === k
                  ? 'border-primary bg-accent font-medium text-accent-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              {chipLabel(k)}
            </button>
          ))}
        </div>
        <div className="ms-auto flex flex-wrap gap-2">
          {staff ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={m.rematch.isPending}
              onClick={() => m.rematch.mutate()}
            >
              <RefreshCw className="size-4" /> {t('taxPages.ims.matchAgain')}
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              void downloadImsCsv(orgId, period, v.gstin ?? 'GSTIN').catch((e: Error) =>
                toast(e.message, { tone: 'error' }),
              )
            }
          >
            <Download className="size-4" /> {t('taxPages.ims.downloadWorksheet')}
          </Button>
        </div>
      </div>

      {staff && picked.size ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
          data-testid="ims-bulk"
        >
          <span className="font-medium">{t('taxPages.ims.selected', { n: picked.size })}</span>
          <Button size="sm" disabled={m.act.isPending} onClick={() => act('accept')}>
            {t('taxPages.ims.accept')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={m.act.isPending}
            onClick={() => act('pending')}
          >
            {t('taxPages.ims.keepPending')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={m.act.isPending}
            onClick={() => act('reject')}
          >
            {t('taxPages.ims.reject')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={m.act.isPending}
            onClick={() => act('no_action')}
          >
            {t('taxPages.ims.clearAction')}
          </Button>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="ims-table">
          <TableHeader>
            <TableRow>
              {staff ? (
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    aria-label={t('taxPages.ims.selectAll')}
                    className="size-4 accent-primary"
                    checked={allPicked}
                    onChange={() =>
                      setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.id)))
                    }
                  />
                </TableHead>
              ) : null}
              <TableHead>{t('taxPages.ims.colSupplier')}</TableHead>
              <TableHead>{t('taxPages.vatReturn.colType')}</TableHead>
              <TableHead>{t('taxPages.eInvoicing.col.number')}</TableHead>
              <TableHead>{t('taxPages.eInvoicing.col.date')}</TableHead>
              <TableHead className="text-end">{t('taxPages.ims.colTaxable')}</TableHead>
              <TableHead className="text-end">GST</TableHead>
              <TableHead>{t('taxPages.ims.colMatch')}</TableHead>
              <TableHead>{t('taxPages.ims.colInBooks')}</TableHead>
              <TableHead>{t('taxPages.ims.colAction')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <Row
                key={r.id}
                r={r}
                staff={staff}
                picked={picked.has(r.id)}
                onPick={() =>
                  setPicked((p) => {
                    const n = new Set(p);
                    if (n.has(r.id)) n.delete(r.id);
                    else n.add(r.id);
                    return n;
                  })
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {v.notOnPortal.length ? (
        <section data-testid="ims-not-on-portal">
          <h2 className="mb-1 text-base font-semibold">{t('taxPages.ims.notReportedTitle')}</h2>
          <p className="mb-2 text-sm text-muted-foreground">{t('taxPages.ims.notReportedHint')}</p>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('taxPages.ims.colVendor')}</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>{t('taxPages.ims.colBillNo')}</TableHead>
                  <TableHead>{t('taxPages.eInvoicing.col.date')}</TableHead>
                  <TableHead className="text-end">{t('taxPages.ims.colTaxable')}</TableHead>
                  <TableHead className="text-end">GST</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.notOnPortal.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Link
                        className="text-primary hover:underline"
                        to={`${b.kind === 'debit_note' ? '/expenses/debit-notes' : '/expenses/bills'}/${b.id}`}
                      >
                        {b.vendor}
                      </Link>
                    </TableCell>
                    <TableCell className="tnum text-xs">{b.supplierGstin}</TableCell>
                    <TableCell>{b.reference}</TableCell>
                    <TableCell>{formatDate(b.date)}</TableCell>
                    <TableCell className="tnum text-end">{formatAmount(b.taxable)}</TableCell>
                    <TableCell className="tnum text-end">{formatAmount(b.tax)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {t('taxPages.ims.lastImport', {
          file: ltrIn(v.imports[0]!.fileName ?? v.imports[0]!.source),
          rows: v.imports[0]!.rows,
        })}
        {v.imports[0]!.warnings.length ? ` · ${v.imports[0]!.warnings.join(' ')}` : ''}
      </p>
    </div>
  );
}

function Row({
  r,
  staff,
  picked,
  onPick,
}: {
  r: ImsRow;
  staff: boolean;
  picked: boolean;
  onPick: () => void;
}) {
  const t = useT();
  const diff = r.differences
    .map((d) =>
      t('taxPages.ims.difference', {
        field: fieldLabel(d.field),
        portal: d.portal,
        books: d.books,
      }),
    )
    .join('\n');
  return (
    <TableRow data-testid="ims-row">
      {staff ? (
        <TableCell>
          <input
            type="checkbox"
            aria-label={t('taxPages.ims.selectRow', { number: r.number })}
            className="size-4 accent-primary"
            checked={picked}
            onChange={onPick}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <div className="font-medium">{r.supplierName ?? r.supplierGstin}</div>
        <div className="tnum text-xs text-muted-foreground">{r.supplierGstin}</div>
      </TableCell>
      <TableCell className="text-xs">
        {r.docType === 'invoice'
          ? t('taxPages.ims.docInvoice')
          : r.docType === 'credit_note'
            ? t('taxPages.ims.docCreditNote')
            : t('taxPages.ims.docDebitNote')}
        {r.reverseCharge ? ' · RCM' : ''}
      </TableCell>
      <TableCell>{r.number}</TableCell>
      <TableCell>{formatDate(r.date)}</TableCell>
      <TableCell className="tnum text-end">{formatAmount(r.taxable)}</TableCell>
      <TableCell className="tnum text-end">{rupees(tax(r))}</TableCell>
      <TableCell>
        <span
          className={cn('rounded-full px-2 py-0.5 text-xs font-medium', MATCH_TONE[r.matchStatus])}
          title={diff || undefined}
        >
          {matchLabel(r.matchStatus)}
        </span>
        {r.differences.length ? (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {r.differences.map((d) => fieldLabel(d.field)).join(', ')}
          </div>
        ) : null}
        {r.itcAvailable === false ? (
          <div className="mt-0.5 text-xs text-[#B42318]">
            {t('taxPages.ims.itcNotAvailable')}
            {r.itcReason ? `: ${r.itcReason}` : ''}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        {r.matched ? (
          <Link
            className="text-primary hover:underline"
            to={`${r.matched.kind === 'debit_note' ? '/expenses/debit-notes' : '/expenses/bills'}/${r.matched.id}`}
          >
            {r.matched.reference ?? r.matched.number}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">
        <span
          className={cn(
            r.action === 'reject' && 'text-[#B42318]',
            r.action === 'accept' && 'text-[#067647]',
          )}
        >
          {actionLabel(r.action)}
        </span>
        {r.actionRemark ? <div className="text-muted-foreground">{r.actionRemark}</div> : null}
      </TableCell>
    </TableRow>
  );
}

function Stat({ label, value, onClick }: { label: string; value: number; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="rounded-lg border bg-card px-3 py-2 text-start enabled:hover:bg-muted"
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="tnum text-xl font-semibold">{value}</div>
    </button>
  );
}
